import React, { useCallback, useMemo, useRef } from "react";
import {
  SearchOption,
  TypeaheadInput,
  TypeaheadInputComponent,
  toast,
  ErrorMessage,
  Flex,
  Text,
  Tag,
} from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { addError } from "@jsq/observability";
import { formatError } from "#common/hooks/useReportError";
import { useGetPayeesForPayeeFilterLazyQuery } from "#graphql/supergraphHooks";
import { clientContext } from "#utils/graphql_utils";
import { getPayeeReviewTag } from "../../utils";
import { PayeeEdgeType, PayeeFilterProps } from "./types";

const CREATE_NEW_PAYEE_OPTION_ID = "create-new-payee";

export const PayeeFilter: React.FC<PayeeFilterProps> = ({
  onPayeeSelect,
  error,
  disabled = false,
  arenaId,
  onCreateNewPayee,
  selectedPayee,
}: PayeeFilterProps) => {
  const [fetchPayeesQuery, { data, loading }] = useGetPayeesForPayeeFilterLazyQuery();

  const handleDebouncedSearch = useCallback(
    async (value?: string) => {
      if (disabled) return;

      try {
        const filters: { searchTerm?: string; arenaIds?: string[] } = { searchTerm: value };
        if (arenaId) {
          filters.arenaIds = [arenaId];
        }

        // useLazyQuery does NOT reject on Apollo errors — it resolves with the
        // error captured on the result tuple. Inspect `result.error` so Apollo
        // failures (network or GraphQL) are reported consistently. Mirrors the
        // pattern in ClientSelector.
        const result = await fetchPayeesQuery({
          context: clientContext(),
          variables: {
            filters,
          },
          fetchPolicy: "no-cache",
        });
        if (result.error) {
          addError(formatError(result.error));
          toast.error("Failed to search for payees. Please try again.");
        }
      } catch (err) {
        // Unexpected non-Apollo errors (e.g. JS runtime bugs) end up here.
        addError(formatError(err));
        toast.error("Failed to search for payees. Please try again.");
      }
    },
    [fetchPayeesQuery, disabled, arenaId],
  );

  // Effect to handle refresh trigger

  const typeaheadRef = useRef<TypeaheadInputComponent>(null);

  const handlePayeeSelection = useCallback(
    (value?: SearchOption) => {
      if (value) {
        // Handle "Create New Payee" option
        if (value.id === CREATE_NEW_PAYEE_OPTION_ID && onCreateNewPayee) {
          onCreateNewPayee();
          return;
        }

        // Handle regular payee selection
        if (onPayeeSelect) {
          const partialPayeeData = data?.payees?.edges.find(
            (payee: PayeeEdgeType) => payee.node.id === value.id,
          )?.node;

          if (partialPayeeData) {
            onPayeeSelect(partialPayeeData);
          }
        }
      }
    },
    [onPayeeSelect, onCreateNewPayee, data],
  );

  const payeeList = useMemo(() => {
    const regularPayees =
      data?.payees?.edges.map((payee: PayeeEdgeType) => {
        const watchlistStatuses = payee.node.paymentPref?.watchlistStatus;
        const baseLabel = `${payee.node.name} | ${payee.node.paymentPref.bankInfoRedacted} | (${payee.node.payeeType.charAt(0).toUpperCase() + payee.node.payeeType.slice(1)})`;
        const reviewTag = getPayeeReviewTag(payee.node.status, watchlistStatuses);

        if (reviewTag) {
          const tagText =
            reviewTag === "pending-approval" ? "Pending review" : "Pending screening review";
          return {
            id: payee.node.id,
            label: baseLabel,
            customLabel: (
              <Flex
                alignItems="center"
                gap={8}
                justifyContent="space-between"
                style={{ width: "100%" }}
              >
                <Text variant="regular-md">{baseLabel}</Text>
                <Tag variant="warning">{tagText}</Tag>
              </Flex>
            ),
          };
        }

        return {
          id: payee.node.id,
          label: baseLabel,
        };
      }) || [];

    if (onCreateNewPayee && !disabled) {
      return [
        ...regularPayees,
        {
          id: CREATE_NEW_PAYEE_OPTION_ID,
          label: "Create a new payee",
          customLabel: (
            <Flex alignItems="center" gap={8}>
              <Icon type="plus" style={{ color: Colors.Primary600 }} />
              <Text variant="strong-md" style={{ color: Colors.Primary600 }}>
                Create a new payee
              </Text>
            </Flex>
          ),
        },
      ];
    }

    return regularPayees;
  }, [data, onCreateNewPayee, disabled]);

  const displayError = useMemo(() => {
    if (error) {
      return error;
    }

    return null;
  }, [error]);

  return (
    <Flex flexDirection="column" gap={16}>
      <TypeaheadInput
        ref={typeaheadRef}
        data-testid="payee-filter"
        key={selectedPayee?.id}
        isClearable
        isFullWidth
        disabled={disabled}
        isLoading={loading}
        options={payeeList}
        onSelectOption={handlePayeeSelection}
        onDebouncedSearch={handleDebouncedSearch}
        placeholder={disabled ? "Select a client first" : "Search for a payee"}
        initialSearchValue={selectedPayee?.name || ""}
      />
      {displayError && <ErrorMessage>{displayError}</ErrorMessage>}
    </Flex>
  );
};
